import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ReactComponent as CheckIcon } from '@repo/common/assets/icons/16px/icon_16px_check.svg';
import {
  addOpsFile,
  addDefaultFile,
  addOpsDefaultFiles,
  getDefaultFiles,
  getOpsFiles,
  removeDefaultFile,
  updateDefaultFile,
  uploadDefaultFile,
  removeOpsFile,
  updateOpsFile,
  uploadOpsFile,
} from '../../../api/twoDays/ops.api';
import TdButton from '../Button';
import { cn } from '../model/derive';
import {
  FILE_ACCEPT,
  FILE_TYPES_LABEL,
  GROUPS,
  LINK_TYPES,
  MAX_FILES,
  MAX_FILE_BYTES,
  ROLES,
  baseName,
  filesOf,
  groupLabel,
  guessLink,
  isDrive,
  isLinkUrl,
  DRIVE_MSG,
  metaOf,
  pilotRoles,
  rolesLabel,
  sizeLabel,
  typeOf,
  updatedLabel,
} from '../model/teamFiles';
import { Field, Modal, ModalButton, Select, TextInput } from '../Modal';

/**
 * Ops › Setup › Brief › Team files — 워크보드 Resources 에 보이는 파일·링크.
 * 시작 전 · 진행 중 · 끝난 뒤 언제든 더하고 고치고 지운다.
 * 시작 뒤 새로 더하거나 파일·링크를 바꾸면 워크보드에 "Updated" 가 붙는다 (알림은 보내지 않는다).
 */

const COLS = 'grid grid-cols-[minmax(220px,1fr)_136px_132px_96px_72px_80px] items-center';
const LINK_BTN =
  'text-[14px] font-medium text-text-primary underline-offset-2 hover:underline disabled:cursor-default disabled:text-text-disable disabled:no-underline';

const EVENT = 'td-team-files';

/** Team files 가 바뀌면 알린다 (Start / End 단계가 듣는다) */
const announceTeamFiles = (projectId, files) =>
  window.dispatchEvent(new CustomEvent(EVENT, { detail: { projectId, files } }));

/**
 * 프로젝트 팀 파일 — 처음엔 서버에서 받고, Brief 의 Team files 에서 바뀌면 따라 바뀐다.
 * @returns files 배열 (불러오기 전 null)
 */
export const useTeamFiles = (projectId) => {
  const [files, setFiles] = useState(null);
  useEffect(() => {
    let alive = true;
    getOpsFiles(projectId)
      .then((list) => alive && setFiles((cur) => cur ?? list ?? []))
      .catch(() => {});
    const on = (e) => {
      if (e.detail.projectId === projectId) setFiles(e.detail.files);
    };
    window.addEventListener(EVENT, on);
    return () => {
      alive = false;
      window.removeEventListener(EVENT, on);
    };
  }, [projectId]);
  return files;
};

/** 안내 상자 — tone: default(초록) · warning(노랑) */
export const FilesAlert = ({ tone = 'default', title, children }) => (
  <div
    role={tone === 'warning' ? 'status' : 'note'}
    className={cn(
      'flex items-start gap-2.5 rounded-xl border px-4 py-3 text-[14px] leading-[150%]',
      tone === 'warning'
        ? 'border-functional-warning02 bg-functional-warning01 text-functional-warning04'
        : 'border-green-deep02/20 bg-surface-green10 text-text-primary',
    )}
  >
    <span
      aria-hidden
      className={cn(
        'mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full',
        tone === 'warning' ? 'bg-functional-warning03' : 'bg-green-deep02',
      )}
    />
    <span className='flex flex-col'>
      {title && <span className='font-medium'>{title}</span>}
      <span className={cn(title && 'text-text-secondary')}>{children}</span>
    </span>
  </div>
);

/** 칩 버튼 — 그룹(하나만) · 역할(여러 개) */
const ChipButton = ({ on, check, onClick, children }) => (
  <button
    type='button'
    aria-pressed={on}
    onClick={onClick}
    className={cn(
      'flex h-10 items-center gap-1.5 rounded-full border px-4 text-[14px] transition-colors',
      on
        ? 'border-green-deep02 font-medium text-green-deep04'
        : 'border-green-deep03/20 text-text-primary hover:bg-grayscale-gray01',
    )}
  >
    {check && on && <CheckIcon className='h-3.5 w-3.5' />}
    {children}
  </button>
);

const EMPTY = {
  kind: 'file',
  // 파일 여러 개 — { file } 새로 고른 것 · { key, fileName, size } 지금 있는 것
  items: [],
  url: '',
  linkType: 'Figma',
  name: '',
  group: '',
  roles: [],
};

/** 추가·수정 모달. file 이 있으면 수정 */
const FileDialog = ({ open, file, started, onClose, onSubmit }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [typeTouched, setTypeTouched] = useState(false);
  const [fileError, setFileError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setBusy(false);
    setFileError('');
    setTypeTouched(!!file);
    setForm(
      file
        ? {
            kind: file.kind,
            items: filesOf(file)
              .filter((x) => x.key)
              .map((x) => ({ key: x.key, fileName: x.fileName, size: x.size })),
            url: file.kind === 'link' ? file.url : '',
            // 예전의 Google Drive 종류는 없앴다 — 주소로 다시 고른다
            linkType: LINK_TYPES.some((t) => t.key === file.linkType)
              ? file.linkType
              : guessLink(file.url ?? ''),
            name: file.name,
            group: file.group,
            // 예전에 고른 Marketer 는 빼고 연다 (파일럿 역할만)
            roles: pilotRoles(file.roles),
          }
        : EMPTY,
    );
  }, [open, file]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  /** 파일 고르기 — 여러 개를 한 번에 골라도 된다 (한 항목에 최대 5개) */
  const pick = (list) => {
    const picked = Array.from(list ?? []);
    if (!picked.length) return;
    const bad = picked.find((f) => !typeOf(f.name));
    if (bad) {
      setFileError(`Use a ${FILE_TYPES_LABEL} file.`);
      return;
    }
    if (picked.some((f) => f.size > MAX_FILE_BYTES)) {
      setFileError('Files can be up to 50 MB.');
      return;
    }
    if (form.items.length + picked.length > MAX_FILES) {
      setFileError(`You can add up to ${MAX_FILES} files.`);
      return;
    }
    setFileError('');
    setForm((cur) => ({
      ...cur,
      items: [...cur.items, ...picked.map((f) => ({ file: f }))],
      name: cur.name.trim() ? cur.name : baseName(picked[0].name),
    }));
  };
  const removeItem = (i) => {
    setFileError('');
    setForm((cur) => ({ ...cur, items: cur.items.filter((_, j) => j !== i) }));
  };

  const drive = form.kind === 'link' && isLinkUrl(form.url) && isDrive(form.url);
  const hasSource = form.kind === 'file' ? form.items.length > 0 : isLinkUrl(form.url) && !drive;
  const reason = !hasSource
    ? form.kind === 'file'
      ? 'Choose at least one file'
      : drive
        ? 'Fix the link marked in red'
        : 'Add a link'
    : !form.name.trim()
      ? 'Add a name'
      : !form.group
        ? 'Pick a group'
        : form.roles.length === 0
          ? 'Pick at least one role'
          : null;
  // 시작 뒤 새로 더하거나 파일·링크가 바뀌면 워크보드에 Updated 가 붙는다
  const sourceChanged =
    !file ||
    form.kind !== file.kind ||
    (form.kind === 'file'
      ? form.items.map((x) => x.key ?? '').join('|') !==
        filesOf(file)
          .map((x) => x.key ?? '')
          .join('|')
      : form.url.trim() !== file.url || form.linkType !== file.linkType);

  const submit = async () => {
    if (reason || busy) return;
    setBusy(true);
    const ok = await onSubmit(form, sourceChanged);
    if (!ok) setBusy(false);
  };

  return (
    <Modal
      open={open}
      title={file ? 'Edit file' : 'Add file or link'}
      onClose={onClose}
      width='max-w-[520px]'
      sheetOnMobile
    >
      <div
        role='tablist'
        aria-label='File or link'
        className='grid grid-cols-2 rounded-full bg-green-deep03/[0.06] p-1'
      >
        {['file', 'link'].map((k) => (
          <button
            key={k}
            type='button'
            role='tab'
            aria-selected={form.kind === k}
            onClick={() => set({ kind: k })}
            className={cn(
              'h-9 rounded-full text-[14px] transition-colors',
              form.kind === k
                ? 'bg-grayscale-gray00 font-medium text-text-primary shadow-4'
                : 'text-text-secondary hover:text-text-primary',
            )}
          >
            {k === 'file' ? 'File' : 'Link'}
          </button>
        ))}
      </div>

      {form.kind === 'file' ? (
        <Field label='Files' required>
          <input
            ref={inputRef}
            type='file'
            accept={FILE_ACCEPT}
            multiple
            className='hidden'
            onChange={(e) => {
              pick(e.target.files);
              e.target.value = '';
            }}
          />
          {form.items.length > 0 ? (
            <div className='flex flex-col gap-2'>
              <ul className='flex flex-col divide-y divide-green-deep03/[0.12] rounded-xl border border-green-deep03/[0.12]'>
                {form.items.map((x, i) => {
                  const name = x.file?.name ?? x.fileName;
                  const size = x.file?.size ?? x.size;
                  return (
                    // eslint-disable-next-line react/no-array-index-key
                    <li
                      key={`${name}-${i}`}
                      className='flex items-center justify-between gap-3 px-4 py-3'
                    >
                      <span className='flex min-w-0 flex-col'>
                        <span className='truncate text-[14px] font-medium text-text-primary'>
                          {name}
                        </span>
                        <span className='text-[12px] text-text-secondary'>
                          {[typeOf(name), sizeLabel(size), !x.file && 'current']
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      </span>
                      <TdButton
                        variant='ghost'
                        size='s'
                        aria-label={`Remove ${name}`}
                        onClick={() => removeItem(i)}
                      >
                        Remove
                      </TdButton>
                    </li>
                  );
                })}
              </ul>
              {form.items.length < MAX_FILES && (
                <TdButton
                  variant='ghost'
                  size='s'
                  onClick={() => inputRef.current?.click()}
                  className='w-fit border-dashed border-green-deep03/40 px-3 text-green-deep02 hover:bg-surface-green10 hover:text-green-deep02'
                >
                  + Add another file
                </TdButton>
              )}
            </div>
          ) : (
            <button
              type='button'
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                pick(e.dataTransfer.files);
              }}
              className='group flex flex-col items-center gap-1 rounded-xl border border-dashed border-green-deep03/20 bg-grayscale-gray00 px-4 py-6 text-center transition-colors hover:border-green-deep02/40 hover:bg-surface-green10'
            >
              <span className='text-[14px] font-medium text-green-deep03'>Choose files</span>
              <span className='text-[13px] text-text-secondary'>
                {FILE_TYPES_LABEL} · up to {MAX_FILES} files
              </span>
            </button>
          )}
          {fileError && (
            <p role='alert' className='text-[12px] text-functional-failed03'>
              {fileError}
            </p>
          )}
        </Field>
      ) : (
        <Field label='Link' required htmlFor='team-file-url'>
          <div className='flex gap-2'>
            <Select
              aria-label='Link type'
              value={form.linkType}
              onChange={(e) => {
                setTypeTouched(true);
                set({ linkType: e.target.value });
              }}
              className='h-11 w-[148px] shrink-0 rounded-full'
            >
              {LINK_TYPES.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.key}
                </option>
              ))}
            </Select>
            <TextInput
              id='team-file-url'
              value={form.url}
              placeholder={LINK_TYPES.find((t) => t.key === form.linkType)?.placeholder}
              onChange={(e) => {
                const url = e.target.value;
                // 고른 적이 없으면 주소로 종류를 추천한다
                set(typeTouched || !isLinkUrl(url) ? { url } : { url, linkType: guessLink(url) });
              }}
              className={cn(
                'h-11 rounded-full',
                form.url.trim() && (!isLinkUrl(form.url) || drive) && 'border-functional-failed03',
              )}
            />
          </div>
          {form.url.trim() && (!isLinkUrl(form.url) || drive) && (
            <p role='alert' className='text-[12px] text-functional-failed03'>
              {drive ? DRIVE_MSG : 'Paste a full link that starts with https://'}
            </p>
          )}
        </Field>
      )}

      <Field label='Name' required htmlFor='team-file-name'>
        <TextInput
          id='team-file-name'
          value={form.name}
          maxLength={200}
          placeholder='e.g. Rider app UI kit'
          onChange={(e) => set({ name: e.target.value })}
        />
      </Field>

      <div className='flex flex-col gap-2'>
        <span className='text-[13px] font-medium text-text-primary'>
          Group<span className='text-functional-failed03'>*</span>
        </span>
        <div role='radiogroup' aria-label='Group' className='flex flex-wrap gap-2'>
          {GROUPS.map((g) => (
            <ChipButton key={g.key} on={form.group === g.key} onClick={() => set({ group: g.key })}>
              {g.label}
            </ChipButton>
          ))}
        </div>
      </div>

      <div className='flex flex-col gap-2'>
        <span className='text-[13px] font-medium text-text-primary'>
          Roles<span className='text-functional-failed03'>*</span>{' '}
          <span className='font-normal text-text-secondary'>Who uses it · pick one or more</span>
        </span>
        <div className='flex flex-wrap gap-2'>
          {ROLES.map((r) => {
            const on = form.roles.includes(r);
            return (
              <ChipButton
                key={r}
                on={on}
                check
                onClick={() =>
                  set({
                    roles: on
                      ? form.roles.filter((x) => x !== r)
                      : ROLES.filter((x) => x === r || form.roles.includes(x)),
                  })
                }
              >
                {r}
              </ChipButton>
            );
          })}
        </div>
      </div>

      {started && sourceChanged && (
        <FilesAlert>
          Members see “Updated” on this file in Resources. No notification is sent.
        </FilesAlert>
      )}

      <div className='mt-2 flex items-center justify-end gap-2'>
        {reason && <span className='mr-auto text-[13px] text-text-secondary'>{reason}</span>}
        <ModalButton onClick={onClose}>Cancel</ModalButton>
        <ModalButton primary disabled={!!reason} loading={busy} onClick={submit}>
          {file ? 'Save' : 'Add'}
        </ModalButton>
      </div>
    </Modal>
  );
};

/** 저장소 — 프로젝트 Team files 또는 기본 Resources(운영진이 넣는 것). 같은 화면으로 고친다 */
const projectStore = (id) => ({
  list: () => getOpsFiles(id),
  add: (body) => addOpsFile(id, body),
  update: (fileId, body) => updateOpsFile(id, fileId, body),
  remove: (fileId) => removeOpsFile(id, fileId),
  upload: (file) => uploadOpsFile(id, file),
});
const DEFAULTS_STORE = {
  list: getDefaultFiles,
  add: addDefaultFile,
  update: updateDefaultFile,
  remove: removeDefaultFile,
  upload: uploadDefaultFile,
};

/**
 * @param defaults true = 기본 Resources 보관함 (프로젝트가 아님) — 시작 여부 · 경고 · 기본값 버튼 없이
 */
const TeamFiles = ({ projectId, mode, toast, defaults = false }) => {
  const started = !defaults && mode !== 'draft';
  const store = useMemo(
    () => (defaults ? DEFAULTS_STORE : projectStore(projectId)),
    [defaults, projectId],
  );
  const [showDefaults, setShowDefaults] = useState(false);
  const [files, setFiles] = useState(null);
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState({ open: false, file: null });
  // 지우기 확인 — 닫히는 동안에도 이름이 남도록 대상은 따로 둔다
  const [removing, setRemoving] = useState(null);
  const [removeOpen, setRemoveOpen] = useState(false);

  const load = useCallback(() => {
    store
      .list()
      .then((list) => setFiles(list ?? []))
      .catch(() => setError('Couldn’t load team files. Refresh the page.'));
  }, [store]);
  useEffect(() => {
    load();
  }, [load]);
  // Start / End 단계가 브리프·파일 유무를 같이 본다
  useEffect(() => {
    if (files && !defaults) announceTeamFiles(projectId, files);
  }, [projectId, files, defaults]);

  /** 저장 — 서버가 돌려준 목록으로 바꾼다. 성공하면 true */
  const run = async (call, message) => {
    try {
      setError('');
      setFiles(await call());
      if (message) toast?.(message);
      return true;
    } catch (e) {
      setError(e.response?.data?.msg || 'Couldn’t save. Try again.');
      return false;
    }
  };

  const others = (files ?? []).filter((f) => !f.pinned);

  const submit = async (form, sourceChanged) => {
    const editing = dialog.file;
    // 새로 고른 파일만 올리고, 지금 있는 파일은 key 로 남긴다 (순서 유지)
    const uploadAll = async () => {
      const files = [];
      for (const x of form.items) {
        // eslint-disable-next-line no-await-in-loop
        files.push(x.file ? await store.upload(x.file) : x);
      }
      return { files };
    };
    const source =
      form.kind === 'file'
        ? await uploadAll().catch(() => null)
        : { url: form.url.trim(), linkType: form.linkType };
    if (!source) {
      setError('Couldn’t upload the file. Try again.');
      return false;
    }
    const body = {
      kind: form.kind,
      name: form.name.trim(),
      group: form.group,
      roles: form.roles,
      ...source,
    };
    const ok = await run(
      () => (editing ? store.update(editing.id, body) : store.add(body)),
      editing
        ? started && sourceChanged
          ? 'File updated · shows “Updated” on the workboard'
          : 'Changes saved'
        : started
          ? 'File added · shows “Updated” on the workboard'
          : 'File added',
    );
    if (ok) setDialog((d) => ({ ...d, open: false }));
    return ok;
  };

  /** 기본 Resources 중 빠진 것만 채운다 — 기본값이 생기기 전에 만든 프로젝트 · 실수로 지운 항목 */
  const [filling, setFilling] = useState(false);
  const fillDefaults = async () => {
    setFilling(true);
    const before = others.length;
    let added = 0;
    const ok = await run(async () => {
      const list = await addOpsDefaultFiles(projectId);
      added = (list ?? []).filter((f) => !f.pinned).length - before;
      return list;
    });
    setFilling(false);
    if (!ok) return;
    toast?.(
      added > 0
        ? `${added} default ${added === 1 ? 'file' : 'files'} added${started ? ' · shows “Updated” on the workboard' : ''}`
        : 'All default files are already here',
    );
  };

  const remove = async () => {
    const target = removing;
    const ok = await run(
      () => store.remove(target.id),
      defaults ? 'Removed from default resources' : 'File removed',
    );
    if (ok) setRemoveOpen(false);
  };

  return (
    <section
      aria-labelledby={defaults ? 'default-files-title' : 'team-files-title'}
      className={cn(
        'flex flex-col gap-3',
        defaults &&
          'rounded-2xl bg-green-deep03/[0.03] p-4 shadow-[inset_0_0_0_1px_rgba(0,158,79,0.12)]',
      )}
    >
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <h3
          id={defaults ? 'default-files-title' : 'team-files-title'}
          className='text-[15px] font-medium text-text-primary'
        >
          {defaults ? 'Default resources' : 'Resources'}
        </h3>
        <div className='flex flex-wrap items-center gap-2'>
          {!defaults && (
            <>
              <TdButton
                variant='ghost'
                aria-expanded={showDefaults}
                onClick={() => setShowDefaults((v) => !v)}
              >
                {showDefaults ? 'Hide default resources' : 'Default resources'}
              </TdButton>
              <TdButton variant='ghost' disabled={!files || filling} onClick={fillDefaults}>
                {filling ? 'Adding…' : 'Add default files'}
              </TdButton>
            </>
          )}
          <TdButton
            variant='soft'
            disabled={!files}
            onClick={() => setDialog({ open: true, file: null })}
          >
            Add file or link
          </TdButton>
        </div>
      </div>
      {defaults ? (
        <FilesAlert>
          Every new project gets a copy of these files. For a project that already exists, use Add
          default files — it adds only the ones it doesn’t have yet. Removing a file here doesn’t
          remove it from projects that already have it.
        </FilesAlert>
      ) : (
        <FilesAlert>Visible to all members. Don’t upload internal ops files.</FilesAlert>
      )}
      {!defaults && showDefaults && <TeamFiles defaults toast={toast} />}
      {files && !defaults && !started && others.length === 0 && (
        <FilesAlert tone='warning' title='No team files added'>
          You can still start the project.
        </FilesAlert>
      )}
      {error && (
        <p role='alert' className='text-[13px] text-functional-failed03'>
          {error}
        </p>
      )}

      <div className='overflow-x-auto rounded-xl border border-green-deep03/[0.12] bg-grayscale-gray00'>
        <div role='table' aria-label='Resources' className='min-w-[720px]'>
          <div
            role='row'
            className={cn(COLS, 'h-10 px-4 text-[13px] font-medium text-text-secondary')}
          >
            <span role='columnheader'>File</span>
            <span role='columnheader'>Group</span>
            <span role='columnheader'>Roles</span>
            <span role='columnheader' className='text-center'>
              Download
            </span>
            <span role='columnheader' className='text-center'>
              Edit
            </span>
            <span role='columnheader' className='text-center'>
              Remove
            </span>
          </div>

          {!files && !error && (
            <p className='border-t border-green-deep03/[0.12] px-4 py-6 text-[14px] text-text-secondary'>
              Loading…
            </p>
          )}

          {files && others.length === 0 && (
            <p className='border-t border-green-deep03/[0.12] px-4 py-6 text-[14px] text-text-secondary'>
              No team files yet.
            </p>
          )}
          {others.map((f) => (
            <div
              key={f.id}
              role='row'
              className={cn(
                COLS,
                'border-t border-green-deep03/[0.12] px-4 py-3 text-[14px] transition-colors hover:bg-green-deep03/[0.03]',
              )}
            >
              <span role='cell' className='flex min-w-0 flex-col items-start gap-1 pr-3'>
                <span className='font-medium text-text-primary [overflow-wrap:anywhere]'>
                  {f.name}
                </span>
                {f.updatedAt && <UpdatedChip at={f.updatedAt} />}
                <span className='text-[13px] text-text-secondary'>{metaOf(f)}</span>
                {/* 파일이 여럿이면 하나씩 받는다 */}
                {filesOf(f).length > 1 && (
                  <span className='flex flex-wrap gap-x-3 gap-y-1'>
                    {filesOf(f).map((x) =>
                      x.url ? (
                        <a
                          key={x.key ?? x.fileName}
                          href={x.url}
                          className={cn(LINK_BTN, 'text-[13px]')}
                        >
                          {x.fileName}
                        </a>
                      ) : (
                        <span key={x.key ?? x.fileName} className='text-[13px] text-text-disable'>
                          {x.fileName}
                        </span>
                      ),
                    )}
                  </span>
                )}
              </span>
              <span role='cell' className='text-text-secondary'>
                {groupLabel(f.group)}
              </span>
              <span role='cell' className='text-text-secondary'>
                {rolesLabel(f.roles)}
              </span>
              <span role='cell' className='text-center'>
                {filesOf(f).length > 1 ? (
                  <span className='text-text-secondary'>{filesOf(f).length} files</span>
                ) : f.url ? (
                  <a
                    href={f.url}
                    target={f.kind === 'link' ? '_blank' : undefined}
                    rel={f.kind === 'link' ? 'noopener noreferrer' : undefined}
                    className={LINK_BTN}
                  >
                    {f.kind === 'link' ? 'Open' : 'Download'}
                  </a>
                ) : (
                  <span className='text-text-disable'>—</span>
                )}
              </span>
              <span role='cell' className='text-center'>
                <button
                  type='button'
                  className={LINK_BTN}
                  onClick={() => setDialog({ open: true, file: f })}
                >
                  Edit
                </button>
              </span>
              <span role='cell' className='text-center'>
                <button
                  type='button'
                  className='text-[14px] font-medium text-functional-failed03 underline-offset-2 hover:underline'
                  onClick={() => {
                    setRemoving(f);
                    setRemoveOpen(true);
                  }}
                >
                  Remove
                </button>
              </span>
            </div>
          ))}
        </div>
      </div>

      <FileDialog
        open={dialog.open}
        file={dialog.file}
        started={started}
        onClose={() => setDialog((d) => ({ ...d, open: false }))}
        onSubmit={submit}
      />

      <Modal
        open={removeOpen}
        title={`Remove “${removing?.name ?? ''}”?`}
        subtitle={
          defaults
            ? 'New projects won’t get it. Projects that already have it keep their copy.'
            : 'Members can’t download it from Resources anymore.'
        }
        onClose={() => setRemoveOpen(false)}
        footer={
          <>
            <ModalButton onClick={() => setRemoveOpen(false)}>Cancel</ModalButton>
            <TdButton variant='danger' onClick={remove}>
              Remove
            </TdButton>
          </>
        }
      />
    </section>
  );
};

/** "Updated · Oct 16, 09:40" */
export const UpdatedChip = ({ at }) => (
  <span className='inline-flex h-6 items-center rounded-full bg-surface-green10 px-2.5 text-[12px] font-medium text-green-deep04 shadow-[inset_0_0_0_1px_rgba(0,158,79,0.24)]'>
    Updated · {updatedLabel(at)}
  </span>
);

export default TeamFiles;
